import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, CheckCircleIcon, LinkIcon, MegaphoneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/ui/PageHeader';
import { ADMIN_PAGE_WIDTH, cx } from '../../components/ui/utils';
import {
  disconnectMarketingOAuth,
  getMarketingOAuthConnections,
  startMarketingOAuth,
} from '../../services/marketingConnectionApi';
import { getProspectingSettings, saveProspectingSettings } from '../../services/marketingApi';

const PROVIDER_COPY = {
  ga4: 'Mide sesiones orgánicas, comportamiento y conversiones después del clic.',
  google_ads: 'Permite investigación con Keyword Planner y atribución de campañas de Google Ads.',
  business_profile: 'Conecta la ficha local de Google Maps, reseñas y rendimiento de búsqueda local.',
};

const AdminMarketingConnections = () => {
  const [connections, setConnections] = useState([]);
  const [callbackUrl, setCallbackUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [prospecting, setProspecting] = useState({ googlePlaces: false, hunter: false });
  const [prospectingDraft, setProspectingDraft] = useState({ googlePlacesApiKey: '', hunterApiKey: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [data, prospectingData] = await Promise.all([getMarketingOAuthConnections(), getProspectingSettings()]);
      setConnections(data.providers || []);
      setCallbackUrl(data.callbackUrl || '');
      setProspecting(prospectingData.integrations || { googlePlaces: false, hunter: false });
    } catch (loadError) {
      setError(loadError.message || 'No se pudieron cargar las conexiones.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('marketingOAuth');
    const provider = params.get('provider');
    if (!result) return;
    const label = connections.find((item) => item.id === provider)?.label || 'Google';
    setMessage(result === 'connected' ? `${label} quedó conectado.` : result === 'expired' ? 'La autorización expiró. Intenta conectar nuevamente.' : 'No se pudo completar la autorización de Google.');
    window.history.replaceState({}, document.title, window.location.pathname);
    load();
    // El callback solo se procesa al regresar de Google.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async (provider) => {
    setWorking(provider);
    setError('');
    try {
      const { authorizationUrl } = await startMarketingOAuth(provider);
      window.location.assign(authorizationUrl);
    } catch (connectError) {
      setError(connectError.message || 'No se pudo abrir la autorización de Google.');
      setWorking('');
    }
  };

  const disconnect = async (provider) => {
    setWorking(provider);
    setError('');
    try {
      await disconnectMarketingOAuth(provider);
      setMessage('Conexión desconectada.');
      await load();
    } catch (disconnectError) {
      setError(disconnectError.message || 'No se pudo desconectar.');
    } finally {
      setWorking('');
    }
  };

  const saveProspecting = async () => {
    setWorking('prospecting'); setError('');
    try {
      const result = await saveProspectingSettings(prospectingDraft);
      setProspecting(result.integrations || prospecting);
      setProspectingDraft({ googlePlacesApiKey: '', hunterApiKey: '' });
      setMessage('Conexiones de Prospección guardadas. Las llaves no se vuelven a mostrar.');
    } catch (saveError) {
      setError(saveError.message || 'No se pudieron guardar las conexiones de Prospección.');
    } finally { setWorking(''); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className={cx('mx-auto px-4 py-6 sm:px-6 lg:px-8', ADMIN_PAGE_WIDTH)}>
        <PageHeader title="Conexiones e integraciones" description="Configura conexiones técnicas una sola vez. Mercadeo utiliza estos activos sin pedirte tokens, IDs ni callbacks." />
        <div className="mt-6 rounded-2xl border border-sky-100 bg-sky-50 p-5 text-sm text-sky-900">
          <p className="font-semibold">Callback OAuth único</p>
          <p className="mt-1 break-all text-sky-800">{callbackUrl || 'Cargando...'}</p>
          <p className="mt-2 text-sky-700">Es el mismo callback ya usado por Google Calendar. No necesitas registrar una URL adicional.</p>
        </div>

        {message && <p className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{message}</p>}
        {error && <p className="mt-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}

        {loading ? <p className="mt-8 text-sm text-gray-400">Cargando conexiones...</p> : (
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3"><div><h2 className="font-semibold text-gray-900">Meta — activos y permisos</h2><p className="mt-2 text-sm leading-6 text-gray-500">Cuenta publicitaria, Página, Instagram, Pixel y tokens. Mercadeo los usa automáticamente.</p></div><MegaphoneIcon className="h-6 w-6 shrink-0 text-orange-500" /></div>
              <Link to="/admin/settings/meta-ads" className="mt-5 inline-flex rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600">Administrar activos Meta</Link>
            </article>
            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3"><div><h2 className="font-semibold text-gray-900">Prospección — Google Places y Hunter</h2><p className="mt-2 text-sm leading-6 text-gray-500">Google Places descubre negocios. BBTS busca primero emails públicos en el website; Hunter solo es respaldo y consume créditos.</p></div><LinkIcon className="h-6 w-6 shrink-0 text-indigo-500" /></div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold"><span className={prospecting.googlePlaces ? 'rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700' : 'rounded-full bg-amber-50 px-2.5 py-1 text-amber-700'}>Google Places: {prospecting.googlePlaces ? 'configurada' : 'pendiente'}</span><span className={prospecting.hunter ? 'rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700' : 'rounded-full bg-slate-100 px-2.5 py-1 text-slate-600'}>Hunter: {prospecting.hunter ? 'configurada' : 'opcional'}</span></div>
              <div className="mt-4 space-y-2"><input type="password" value={prospectingDraft.googlePlacesApiKey} onChange={(event) => setProspectingDraft((current) => ({ ...current, googlePlacesApiKey: event.target.value }))} placeholder={prospecting.googlePlaces ? 'Google Places configurada — escribe solo para reemplazar' : 'Google Places API key'} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /><input type="password" value={prospectingDraft.hunterApiKey} onChange={(event) => setProspectingDraft((current) => ({ ...current, hunterApiKey: event.target.value }))} placeholder={prospecting.hunter ? 'Hunter configurada — escribe solo para reemplazar' : 'Hunter API key (opcional)'} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
              <button type="button" onClick={saveProspecting} disabled={working === 'prospecting'} className="mt-4 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">{working === 'prospecting' ? 'Guardando…' : 'Guardar conexiones de Prospección'}</button>
            </article>
            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3"><div><h2 className="font-semibold text-gray-900">Meta — formularios de leads</h2><p className="mt-2 text-sm leading-6 text-gray-500">Webhook y mapeo de formularios instantáneos de Facebook e Instagram hacia Leads.</p></div><MegaphoneIcon className="h-6 w-6 shrink-0 text-sky-500" /></div>
              <Link to="/admin/settings/meta-leads" className="mt-5 inline-flex rounded-lg border border-sky-200 px-3 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50">Administrar formularios Meta</Link>
            </article>
            {connections.map((item) => {
              const connected = item.status === 'connected';
              const busy = working === item.id;
              return (
                <article key={item.id} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-semibold text-gray-900">{item.label}</h2>
                      <p className="mt-2 text-sm leading-6 text-gray-500">{PROVIDER_COPY[item.id]}</p>
                    </div>
                    {connected ? <CheckCircleIcon className="h-6 w-6 shrink-0 text-emerald-500" /> : <LinkIcon className="h-6 w-6 shrink-0 text-gray-300" />}
                  </div>
                  <p className={cx('mt-5 text-sm font-medium', connected ? 'text-emerald-700' : 'text-gray-500')}>
                    {connected ? `Conectado${item.googleEmail ? `: ${item.googleEmail}` : ''}` : 'No conectado'}
                  </p>
                  <div className="mt-4">
                    {connected ? (
                      <button type="button" onClick={() => disconnect(item.id)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60">
                        <XMarkIcon className="h-4 w-4" /> {busy ? 'Desconectando...' : 'Desconectar'}
                      </button>
                    ) : (
                      <button type="button" onClick={() => connect(item.id)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60">
                        {busy ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />} {busy ? 'Abriendo Google...' : 'Conectar con Google'}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketingConnections;
